import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import Cookies from 'js-cookie';
import { API_BASE_URL } from '../../utils/constants';
import PriceFormat_Basic from '../../components/commerce-ui/price-format-basic';

function PaymentSuccess() {
    const [order, setOrder] = useState(null);
    const [status, setStatus] = useState("Verificando...");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const { order_oid: routeOrderOid } = useParams();
    const [searchParams] = useSearchParams();
    const sessionIdParam = searchParams.get("session_id") || searchParams.get("sessionId") || (typeof window !== 'undefined' ? sessionStorage.getItem('last_stripe_session_id') : null);
    // Robust fallback extraction from URL in case router params are missing
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    const match = path.match(/payment-success\/([^/]+)/);
    const derivedOrderOid = routeOrderOid || (match && match[1]) || (typeof window !== 'undefined' ? sessionStorage.getItem('last_order_oid') : null);
    const sessionId = sessionIdParam || (typeof window !== 'undefined' ? new URL(window.location.href).searchParams.get('session_id') : null);

    // Verify payment
    useEffect(() => {
        const verifyPayment = async () => {
            try {
                setLoading(true);
                
                const access_token = Cookies.get('access_token');

                const axiosInstance = axios.create({
                    baseURL: API_BASE_URL,
                    timeout: 10000,
                    headers: {
                        'Content-Type': 'application/json',
                        ...(access_token ? { 'Authorization': `Bearer ${access_token}` } : {})
                    },
                });

                // Resolve session id robustly
                let sid = sessionId;
                if (!sid && typeof window !== 'undefined') {
                    sid = sessionStorage.getItem('last_stripe_session_id');
                }
                if (!sid && derivedOrderOid) {
                    try {
                        const checkoutRes = await axiosInstance.get(`checkout/${derivedOrderOid}/`);
                        const od = checkoutRes.data || {};
                        sid = od.session_id || od.stripe_session_id || od.session || od.checkout_session_id || null;
                        if (sid && typeof window !== 'undefined') {
                            sessionStorage.setItem('last_stripe_session_id', sid);
                        }
                    } catch (_) {}
                }

                if (!derivedOrderOid || !sid) {
                    throw new Error('Falta información del pedido o sesión.');
                }

                const formdata = new FormData();
                formdata.append('order_oid', derivedOrderOid);
                formdata.append('session_id', sid);

                const response = await axiosInstance.post(`payment-success/${derivedOrderOid}/`, formdata);
                const orderData = response.data;
                
                // Handle response and update status based on new backend format
                if (orderData.message === "Payment verified successfully" || 
                    orderData.message === "Payment manually verified") {
                    setStatus("Pago exitoso");
                    // Refresh the page after a short delay to show updated order status
                    setTimeout(() => {
                        window.location.reload();
                    }, 2000);
                } else if (orderData.message === "Order already paid") {
                    setStatus("Ya pagado");
                } else if (orderData.message === "Payment manually verified") {
                    setStatus("Pago verificado");
                } else {
                    setStatus(orderData.message || "Pago verificado");
                }
            } catch (err) {
                const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Falló la verificación del pago';
                setError(errorMessage);
                setStatus("Verificación fallida");
                
                Swal.fire({
                    icon: 'error',
                    title: 'Verificación Fallida',
                    text: errorMessage,
                });
            } finally {
                setLoading(false);
            }
        };

        verifyPayment();
    }, [derivedOrderOid, sessionId]);

    // Fetch order details
    useEffect(() => {
        if (!derivedOrderOid) return;

        const fetchOrder = async () => {
            try {
                setLoading(true);
                
                // Get access token
                const access_token = Cookies.get("access_token");
                
                // Create axios instance for this request
                const axiosInstance = axios.create({
                    baseURL: 'https://lua-cheia-backend-production.up.railway.app/api/v1/',
                    timeout: 10000,
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        ...(access_token && { Authorization: `Bearer ${access_token}` })
                    }
                });

                const response = await axiosInstance.get(`checkout/${derivedOrderOid}/`);
                setOrder(response.data);
            } catch (err) {
                setError("Error al cargar los detalles del pedido");
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'No se pudieron obtener los detalles del pedido. Por favor contacte al soporte.',
                });
            } finally {
                setLoading(false);
            }
        };

        fetchOrder();
    }, [derivedOrderOid]);

    if (error) {
        return (
            <div className="container text-center py-5">
                <i className="fas fa-exclamation-triangle text-danger fa-5x mb-4"></i>
                <h2>{error}</h2>
                <p>Por favor intente actualizar la página o contacte al soporte si el problema persiste.</p>
                <button 
                    className="btn btn-primary mt-3"
                    onClick={() => window.location.reload()}
                >
                    Intentar de Nuevo
                </button>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="container text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                </div>
                <p className="mt-3">Verificación de pago en progreso</p>
            </div>
        );
    }

    return (
        <div className="min-vh-100">
            <main className="mb-4 mt-4">
                <div className="container">
                    <section>
                        <div className="row pb50">
                            <div className="col-lg-12">
                                <div className="dashboard_title_area">
                                    <h4 className="fw-bold text-center mb-4 mt-4">
                                        {status === t("Payment successful") ? t("Payment Verified Successfully!") : t("Payment Status")} 
                                        {status === t("Payment successful") && <i className="fas fa-check-circle ms-2 text-success" />}
                                    </h4>
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-xl-12">
                                <div className="application_statics">
                                    <div className="account_user_deails dashboard_page">
                                        <div className="d-flex justify-content-center align-items-center">
                                            {status === "Verifying..." && (
                                                <div className="col-lg-12">
                                                    <div className="border border-3" />
                                                    <div className="card bg-white shadow p-5">
                                                        <div className="mb-4 text-center">
                                                            <i
                                                                className="fas fa-spinner fa-spin text-primary"
                                                                style={{ fontSize: 100 }}
                                                            />
                                                        </div>
                                                        <div className="text-center">
                                                            <h1>¡Su pago está siendo verificado!</h1>
                                                            <p>
                                                                Espere mientras verificamos su pago. <b>¡No debería tomar más de 1 minuto!</b>
                                                                <br />
                                                                <b className='text-danger'>NOTA: ¡No recargue ni abandone la página!</b>
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {status === "Payment successful" && order && (
                                                <div className="col-lg-12">
                                                    <div className="border border-3 border-success" />
                                                    <div className="card bg-white shadow p-5">
                                                        <div className="mb-4 text-center">
                                                            <i
                                                                className="fas fa-check-circle text-success"
                                                                style={{ fontSize: 100 }}
                                                            />
                                                        </div>
                                                        <div className="text-center">
                                                            <h1>¡Gracias por su compra!</h1>
                                                            <p className="lead">
                                                                Su pago ha sido verificado y procesado exitosamente.
                                                            </p>
                                                            <p>
                                                                La confirmación del pedido ha sido enviada a <b>{order.email}</b>
                                                            </p>
                                                            <div className="d-flex justify-content-center gap-3 mt-4">
                                                                <button
                                                                    className="btn btn-success"
                                                                    data-bs-toggle="modal"
                                                                    data-bs-target="#orderSummaryModal"
                                                                >
                                                                    Ver Detalles del Pedido <i className="fas fa-eye ms-2" />
                                                                </button>
                                                                <a
                                                                    href={`http://${window.location.host}/customer/invoice/${order.oid}/`}
                                                                    className="btn btn-primary"
                                                                >
                                                                    Descargar Factura <i className="fas fa-file-invoice ms-2" />
                                                                </a>
                                                                <a
                                                                    href={`http://${window.location.host}/`}
                                                                    className="btn btn-secondary"
                                                                >
                                                                    Continuar Comprando <i className="fas fa-home ms-2" />
                                                                </a>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {status === "Your purchase is unpaid" && (
                                                <div className="col-lg-12">
                                                    <div className="border border-3 border-danger" />
                                                    <div className="card bg-white shadow p-5">
                                                        <div className="mb-4 text-center">
                                                            <i
                                                                className="fas fa-ban text-danger"
                                                                style={{ fontSize: 100 }}
                                                            />
                                                        </div>
                                                        <div className="text-center">
                                                            <h1>Compra no pagada</h1>
                                                            <p>
                                                                Su pago no se completó exitosamente.
                                                            </p>
                                                            <p className="text-danger mb-4">
                                                                <b>Por favor intente de nuevo o contacte al soporte.</b>
                                                            </p>
                                                            <a href={`http://${window.location.host}/cart`} className="btn btn-danger mt-3">
                                                                Regresar al Carrito
                                                            </a>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {status === "Already paid" && (
                                                <div className="col-lg-12">
                                                    <div className="border border-3 border-warning" />
                                                    <div className="card bg-white shadow p-5">
                                                        <div className="mb-4 text-center">
                                                            <i
                                                                className="fas fa-check-circle text-warning"
                                                                style={{ fontSize: 100 }}
                                                            />
                                                        </div>
                                                        <div className="text-center">
                                                            <h1>Pedido Ya Procesado</h1>
                                                            <p>
                                                                Este pedido ya ha sido pagado y procesado.
                                                            </p>
                                                            <a href={`http://${window.location.host}/orders`} className="btn btn-warning mt-3">
                                                                Ver Sus Pedidos
                                                            </a>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {status === "You cancelled your purchase" && (
                                                <div className="col-lg-12">
                                                    <div className="border border-3 border-danger" />
                                                    <div className="card bg-white shadow p-5">
                                                        <div className="mb-4 text-center">
                                                            <i
                                                                className="fas fa-times-circle text-danger"
                                                                style={{ fontSize: 100 }}
                                                            />
                                                        </div>
                                                        <div className="text-center">
                                                            <h1>{t('Payment Cancelled')}</h1>
                                                            <p>
                                                                {t('You cancelled your payment process.')}
                                                            </p>
                                                            <a href={`http://${window.location.host}/cart`} className="btn btn-danger mt-3">
                                                                {t('Return to Cart')}
                                                            </a>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </main>

            {/* Order Summary Modal */}
            {order && (
                <div className="modal fade" id="orderSummaryModal" tabIndex={-1} aria-hidden="true">
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{t('Order Summary')} - #{order.oid}</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    data-bs-dismiss="modal"
                                    aria-label="Close"
                                />
                            </div>
                            <div className="modal-body">
                                {/* Order Info Section */}
                                <div className="row mb-4">
                                    <div className="col-md-6">
                                        <h6 className="fw-bold">{t('Order Information')}</h6>
                                        <p><strong>{t('Order ID')}:</strong> {order.oid}</p>
                                        <p><strong>{t('Order Date')}:</strong> {order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}</p>
                                        <p><strong>{t('Status')}:</strong> <span className="badge bg-success">{order.payment_status || t('Paid')}</span></p>
                                    </div>
                                    <div className="col-md-6">
                                        <h6 className="fw-bold">{t('Customer Information')}</h6>
                                        <p><strong>{t('Name')}:</strong> {order.full_name || order.customer_name || 'N/A'}</p>
                                        <p><strong>{t('Email')}:</strong> {order.email || 'N/A'}</p>
                                        <p><strong>{t('Phone')}:</strong> {order.mobile || order.phone || 'N/A'}</p>
                                    </div>
                                </div>

                                {/* Shipping Address */}
                                {order.address && (
                                    <div className="mb-4">
                                        <h6 className="fw-bold">{t('Shipping Address')}</h6>
                                        <p>{order.address}</p>
                                        {order.city && <p>{order.city}, {order.state} {order.country}</p>}
                                    </div>
                                )}

                                {/* Order Items */}
                                <div className="mb-4">
                                    <h6 className="fw-bold">{t('Order Items')}</h6>
                                    <div className="table-responsive">
                                        <table className="table table-striped">
                                            <thead>
                                                <tr>
                                                    <th>{t('Product')}</th>
                                                    <th>{t('Variant')}</th>
                                                    <th>{t('Quantity')}</th>
                                                    <th>{t('Price')}</th>
                                                    <th>{t('Total')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {order.items && Array.isArray(order.items) && order.items.length > 0 ? (
                                                    order.items.map((item, index) => (
                                                        <tr key={index}>
                                                            <td>
                                                                <div className="d-flex align-items-center">
                                                                    {item.product?.image && (
                                                                        <img 
                                                                            src={item.product.image} 
                                                                            alt={item.product?.title || t('Product')} 
                                                                            style={{ width: '40px', height: '40px', objectFit: 'cover', marginRight: '10px' }}
                                                                        />
                                                                    )}
                                                                    <div>
                                                                        <strong>{item.product?.title || item.title || 'Product'}</strong>
                                                                        {item.product?.sku && (
                                                                            <>
                                                                                <br />
                                                                                <small className="text-muted">SKU: {item.product.sku}</small>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                {item.color && item.color !== 'No Color' && (
                                                                    <span className="badge bg-primary me-1">{item.color}</span>
                                                                )}
                                                                {item.size && item.size !== 'No Size' && (
                                                                    <span className="badge bg-secondary">{item.size}</span>
                                                                )}
                                                                {(!item.color || item.color === 'No Color') && (!item.size || item.size === 'No Size') && (
                                                                    <span className="text-muted">{t('Standard')}</span>
                                                                )}
                                                            </td>
                                                            <td>{item.qty || item.quantity || 1}</td>
                                                            <td><PriceFormat_Basic value={parseFloat(item.price || 0)} /></td>
                                                            <td><PriceFormat_Basic value={parseFloat(item.total || (parseFloat(item.price || 0) * (item.qty || item.quantity || 1)) || 0)} /></td>
                                                        </tr>
                                                    ))
                                                ) : order.cart_items && Array.isArray(order.cart_items) && order.cart_items.length > 0 ? (
                                                    order.cart_items.map((item, index) => (
                                                        <tr key={index}>
                                                            <td>
                                                                <div className="d-flex align-items-center">
                                                                    {item.product?.image && (
                                                                        <img 
                                                                            src={item.product.image} 
                                                                            alt={item.product?.title || t('Product')} 
                                                                            style={{ width: '40px', height: '40px', objectFit: 'cover', marginRight: '10px' }}
                                                                        />
                                                                    )}
                                                                    <div>
                                                                        <strong>{item.product?.title || item.title || 'Product'}</strong>
                                                                        {item.product?.sku && (
                                                                            <>
                                                                                <br />
                                                                                <small className="text-muted">SKU: {item.product.sku}</small>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                {item.color && item.color !== 'No Color' && (
                                                                    <span className="badge bg-primary me-1">{item.color}</span>
                                                                )}
                                                                {item.size && item.size !== 'No Size' && (
                                                                    <span className="badge bg-secondary">{item.size}</span>
                                                                )}
                                                                {(!item.color || item.color === 'No Color') && (!item.size || item.size === 'No Size') && (
                                                                    <span className="text-muted">{t('Standard')}</span>
                                                                )}
                                                            </td>
                                                            <td>{item.qty || item.quantity || 1}</td>
                                                            <td><PriceFormat_Basic value={parseFloat(item.price || 0)} /></td>
                                                            <td><PriceFormat_Basic value={parseFloat(item.total || (parseFloat(item.price || 0) * (item.qty || item.quantity || 1)) || 0)} /></td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="5" className="text-center text-muted">
                                                            <i className="fas fa-info-circle me-2"></i>
                                                            {t('Order items information not available')}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* Order Totals */}
                                <div className="row">
                                    <div className="col-md-6 offset-md-6">
                                        <table className="table table-borderless">
                                            <tbody>
                                                <tr>
                                                    <td className="text-end"><strong>{t('Subtotal')}:</strong></td>
                                                    <td className="text-end">${(parseFloat(order.sub_total || order.subtotal || 0)).toFixed(2)}</td>
                                                </tr>
                                                {(parseFloat(order.shipping_amount || order.shipping_fee || 0)) > 0 && (
                                                    <tr>
                                                        <td className="text-end"><strong>{t('Shipping')}:</strong></td>
                                                        <td className="text-end">${(parseFloat(order.shipping_amount || order.shipping_fee || 0)).toFixed(2)}</td>
                                                    </tr>
                                                )}
                                                {(parseFloat(order.tax_fee || order.tax_amount || 0)) > 0 && (
                                                    <tr>
                                                        <td className="text-end"><strong>{t('Tax')}:</strong></td>
                                                        <td className="text-end">${(parseFloat(order.tax_fee || order.tax_amount || 0)).toFixed(2)}</td>
                                                    </tr>
                                                )}
                                                <tr className="border-top">
                                                    <td className="text-end"><strong>{t('Total')}:</strong></td>
                                                    <td className="text-end"><strong>${(parseFloat(order.total || 0)).toFixed(2)}</strong></td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* Payment Information */}
                                {order.payment_method && (
                                    <div className="mt-4 p-3 bg-light rounded">
                                        <h6 className="fw-bold">{t('Payment Information')}</h6>
                                        <p><strong>{t('Method')}:</strong> {order.payment_method}</p>
                                        {order.transaction_id && <p><strong>{t('Transaction ID')}:</strong> {order.transaction_id}</p>}
                                        {order.session_id && <p><strong>{t('Session ID')}:</strong> {order.session_id}</p>}
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">{t('Close')}</button>
                                <a 
                                    href={`/customer/invoice/${order.oid}/`} 
                                    className="btn btn-primary"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <i className="fas fa-download me-2"></i>{t('Download Invoice')}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default PaymentSuccess;
